import {
  INITIAL_QUOTE_STATE,
  quoteReducer,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-reducer';
import {
  parsePriceToMicros,
  parseQuantity,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/utils/parse-quote-input';
import {
  QuoteAction,
  QuoteLineItem,
  QuoteState,
  QuoteTotals,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { useMemo, useReducer } from 'react';

interface UseQuoteStateResult {
  /**
   * True when the quote can be saved: an account is selected, the quote has
   * a non-empty name, and there are unsaved edits.
   */
  canSave: boolean;
  dispatch: React.Dispatch<QuoteAction>;
  state: QuoteState;
  totals: QuoteTotals;
}

/** Sum of `ourPrice * quantity` across every line item, in micros. */
const selectTotalAmountInMicros = (lineItems: QuoteLineItem[]): number =>
  lineItems.reduce(
    (sum, item) =>
      sum + parsePriceToMicros(item.ourPrice) * parseQuantity(item.quantity),
    0,
  );

/**
 * Sum of `(competitorPrice - ourPrice) * quantity` across line items where
 * the rep has actually entered a competitor price. Returns `null` when no
 * line has a parseable price, so callers can distinguish "no savings yet"
 * from "not a competitive quote". Skips empty/unparseable competitor
 * prices (the auto-stub default).
 */
const selectTotalSavingsInMicros = (
  lineItems: QuoteLineItem[],
): number | null => {
  const withCompetitorPrice = lineItems.filter(
    (item) => item.competitor && parsePriceToMicros(item.competitor.price) > 0,
  );
  if (withCompetitorPrice.length === 0) {
    return null;
  }
  return withCompetitorPrice.reduce((sum, item) => {
    if (!item.competitor) {
      return sum;
    }
    return (
      sum +
      (parsePriceToMicros(item.competitor.price) -
        parsePriceToMicros(item.ourPrice)) *
        parseQuantity(item.quantity)
    );
  }, 0);
};

/**
 * State hook for the price-quote editor. Composes `quoteReducer` and derives
 * totals (item count, subtotal, competitor savings).
 *
 * `initialState` lets callers hydrate from a loaded quote; omitting it starts
 * fresh from `INITIAL_QUOTE_STATE`.
 *
 * @example
 * ```tsx
 * const { state, dispatch, totals } = useQuoteState();
 * dispatch({ type: 'SET_ACCOUNT', account: { chatId, name } });
 * ```
 */
export const useQuoteState = (
  initialState?: QuoteState,
): UseQuoteStateResult => {
  const [state, dispatch] = useReducer(
    quoteReducer,
    initialState ?? INITIAL_QUOTE_STATE,
  );

  const isCompetitive = state.competitorName !== null;

  const totals = useMemo<QuoteTotals>(
    () => ({
      itemCount: state.lineItems.length,
      totalAmountInMicros: selectTotalAmountInMicros(state.lineItems),
      totalSavingsInMicros: isCompetitive
        ? selectTotalSavingsInMicros(state.lineItems)
        : null,
    }),
    [isCompetitive, state.lineItems],
  );

  const canSave =
    state.account !== null &&
    state.isDirty &&
    state.name.trim() !== '' &&
    state.lineItems.length > 0;

  return { canSave, dispatch, state, totals };
};
